import { Action, Module, Mutation } from "vuex-class-modules"
import { Modules } from "../modules"
import { store } from "../store"
import { BaseModule } from "@/models/BaseModule"
import { apiService } from "@/services"
import { ServerResponse } from "@/models"
import { Endpoint } from "@/services/endpoints"
import {
  JiraImportData,
  JiraImportResponse,
  JiraIssueTypeMappingDto,
  JiraPsToJiraSeverityMappingDto,
  JiraPsToJiraStatusMappingDto,
  JiraSyncEnabledDto,
  JiraTeamSettingDto,
  JiraUser,
  TeamJiraSyncDto
} from "@/models/JiraImport"
import { ItemStatus, ItemType } from "@/models/Item"

export enum JiraImportActions {
  JIRA_IMPORT_START = "JIRA_IMPORT_START",
  JIRA_IMPORT_GET = "JIRA_IMPORT_GET",
  JIRA_IMPORT_USERS = "JIRA_IMPORT_USERS",
  JIRA_IMPORT_TEST = "JIRA_IMPORT_TEST",
  JIRA_IMPORT_USERS_START = "JIRA_IMPORT_USERS_START",
  JIRA_TOKEN = "JIRA_TOKEN",
  JIRA_IMPORT_BACKLOG_START = "JIRA_IMPORT_BACKLOG_START",
  JIRA_IMPORT_BACKLOG_FINISH = "JIRA_IMPORT_BACKLOG_FINISH",
  JIRA_IMPORT_BACKLOG_SYNC_ENABLE = "JIRA_IMPORT_BACKLOG_SYNC_ENABLE",
  JIRA_IMPORT_BACKLOG_SYNC_DISABLE = "JIRA_IMPORT_BACKLOG_SYNC_DISABLE",
  JIRA_IMPORT_STATUS = "JIRA_IMPORT_STATUS",
  JIRA_SYNC_GET_BY_PROJECT = "JIRA_SYNC_GET_BY_PROJECT",
  JIRA_SYNC_CHANGE_PROJECT = "JIRA_SYNC_CHANGE_PROJECT",
  JIRA_SYNC_CHANGE_PS_TO_JIRA = "JIRA_SYNC_CHANGE_PS_TO_JIRA",
  JIRA_SYNC_CHANGE_JIRA_TO_PS = "JIRA_SYNC_CHANGE_JIRA_TO_PS",
  JIRA_SETTING_GET_BY_TEAM = "JIRA_SETTING_GET_BY_TEAM",
  JIRA_MAPPING_PS_TO_JIRA_STATUSES = "JIRA_MAPPING_PS_TO_JIRA_STATUSES",
  JIRA_MAPPING_PS_TO_JIRA_STATUS_DELETE = "JIRA_MAPPING_PS_TO_JIRA_STATUS_DELETE",
  JIRA_MAPPING_JIRA_TO_PS_STATUSES = "JIRA_MAPPING_JIRA_TO_PS_STATUSES",
  JIRA_MAPPING_JIRA_TO_PS_STATUS_DELETE = "JIRA_MAPPING_JIRA_TO_PS_STATUS_DELETE",
  JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPES = "JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPES",
  JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPE_DELETE = "JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPE_DELETE",
  JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPES = "JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPES",
  JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPE_DELETE = "JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPE_DELETE",
  JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL = "JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL",
  JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL_DELETE = "JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL_DELETE",
  JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL = "JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL",
  JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL_DELETE = "JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL_DELETE"
}

@Module
class JiraImportModule extends BaseModule {
  jiraToken = ""

  get [`get/${JiraImportActions.JIRA_TOKEN}`]() {
    return this.jiraToken
  }

  @Mutation
  clearTabs() {}

  @Action
  async [JiraImportActions.JIRA_IMPORT_START](jiraImportData: JiraImportData) {
    const data = await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_START(jiraImportData.accountId), jiraImportData)
    return data
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_GET](accountId: string) {
    const data = await apiService.get<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_START(accountId))
    return data
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_USERS]({ accountId, importId }:{accountId: string, importId: string}) {
    const data = await apiService.get<JiraUser[]>(Endpoint.JIRA_IMPORT_USERS(accountId, importId))
    return this.handleResponse<JiraUser[]>(data, (data) => {
      return data
    })
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_USERS_START]({ accountId, importId }:{accountId: string, importId: string}) {
    await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_USERS_START(accountId, importId))
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_BACKLOG_START]({ accountId, importId }:{accountId: string, importId: string}) {
    await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_BACKLOG_START(accountId, importId))
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_BACKLOG_FINISH](importId:string) {
    await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_BACKLOG_FINISH(importId))
  }


  @Action
  async [JiraImportActions.JIRA_IMPORT_BACKLOG_SYNC_ENABLE]({ accountId, importId }:{accountId: string, importId: string}) {
    await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_BACKLOG_SYNC_ENABLE(accountId, importId))
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_BACKLOG_SYNC_DISABLE]({ accountId, importId }:{accountId: string, importId: string}) {
    await apiService.post<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_BACKLOG_SYNC_DISABLE(accountId, importId))
  }

  @Action
  async [JiraImportActions.JIRA_IMPORT_STATUS]({ accountId }: {accountId:string}) {
    const data = await apiService.get<ServerResponse<JiraImportResponse>>(Endpoint.JIRA_IMPORT_STATUS(accountId))
    return this.handleResponse<ServerResponse<JiraImportResponse>>(data, data => data)
  }

  @Action
  async [JiraImportActions.JIRA_SYNC_GET_BY_PROJECT](projectId: string) {
    const data = await apiService.get<TeamJiraSyncDto[]>(Endpoint.JIRA_SYNC_GET_BY_PROJECT(projectId))
    return this.handleResponse<TeamJiraSyncDto[]>(data, data => data)
  }

  @Action
  async [JiraImportActions.JIRA_SYNC_CHANGE_PROJECT]({ projectId, enable }: { projectId: string, enable: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.JIRA_SYNC_CHANGE_PROJECT(projectId, enable))
    if (data === false) {
      return false
    }

    return this.handleResponse<boolean>(data, data => data)
  }

  @Action
  async [JiraImportActions.JIRA_SYNC_CHANGE_PS_TO_JIRA]({ projectId, teamId, enable }: { projectId: string,teamId: string, enable: boolean }) {
    const data = await apiService.patch<JiraSyncEnabledDto>(Endpoint.JIRA_SYNC_CHANGE_PS_TO_JIRA(projectId, teamId, enable))
    return this.handleResponse<JiraSyncEnabledDto>(data, data => data)
  }

  @Action
  async [JiraImportActions.JIRA_SYNC_CHANGE_JIRA_TO_PS]({ projectId, teamId, enable }: {projectId: string,teamId: string, enable: boolean }) {
    const data = await apiService.patch<JiraSyncEnabledDto>(Endpoint.JIRA_SYNC_CHANGE_JIRA_TO_PS(projectId, teamId, enable))
    return this.handleResponse<JiraSyncEnabledDto>(data, data => data)
  }

  @Action
  async [JiraImportActions.JIRA_SETTING_GET_BY_TEAM]({ accountId, teamId }: { accountId: string, teamId: string }) {
    const data = await apiService.get<JiraTeamSettingDto>(Endpoint.JIRA_SETTING_GET_BY_TEAM(accountId, teamId))
    return this.handleResponse<JiraTeamSettingDto>(data, data => JiraTeamSettingDto.create(data))
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_STATUSES]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraPsToJiraStatusMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_PS_TO_JIRA_STATUSES(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_STATUS_DELETE]({ accountId, teamId, itemType, storyStatus }: { accountId: string, teamId: string, itemType: ItemType, storyStatus: ItemStatus }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_PS_TO_JIRA_STATUS_DELETE(accountId, teamId, itemType, storyStatus))
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_STATUSES]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraPsToJiraStatusMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_JIRA_TO_PS_STATUSES(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_STATUS_DELETE]({ accountId, teamId }: { accountId: string, teamId: string }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_JIRA_TO_PS_STATUS_DELETE(accountId, teamId))
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPES]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraIssueTypeMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPES(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPE_DELETE]({ accountId, teamId, itemType }: { accountId: string, teamId: string, itemType: ItemType }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_PS_TO_JIRA_ISSUE_TYPE_DELETE(accountId, teamId, itemType))
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPES]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraIssueTypeMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPES(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPE_DELETE]({ accountId, teamId }: { accountId: string, teamId: string }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_JIRA_TO_PS_ISSUE_TYPE_DELETE(accountId, teamId))
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraPsToJiraSeverityMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL_DELETE]({ accountId, teamId }: { accountId: string, teamId: string }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_PS_TO_JIRA_SEVERITY_LEVEL_DELETE(accountId, teamId))
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL]({ accountId, teamId, payload }: { accountId: string, teamId: string, payload: JiraPsToJiraSeverityMappingDto[] }) {
    const data = await apiService.put(Endpoint.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL(accountId, teamId), payload)
    return this.handleResponse(data)
  }

  @Action
  async [JiraImportActions.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL_DELETE]({ accountId, teamId }: { accountId: string, teamId: string }) {
    const data = await apiService.delete(Endpoint.JIRA_MAPPING_JIRA_TO_PS_SEVERITY_LEVEL_DELETE(accountId, teamId))
    return this.handleResponse(data)
  }
}


export const jiraImportModule = new JiraImportModule({
  store,
  name: Modules.JIRA_IMPORT,
})
